import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SumsubWebSdk from "@sumsub/websdk-react";
import { useOnboarding } from "../../features/Onboarding/useOnboarding";
import { useKYC } from "../../pages/userPanel/KYC/useKYC";
import { Spinner } from "../../pages/userPanel/Wallet/UserInvestmentsInfo/UserRewards/ClaimAllRewards/ClaimAllRewardsBody/RewardInfo/RewardInfo.styled";
import { useAppSelector } from "../../redux";
import * as S from "./KycModalBody.styled";
import { TokenType } from "../../constants";
import { sumsubLogo } from "../../assets";

type KycModalBodyProps = {
  handleModalClose: () => void;
  onKycVerificationOpen?: (isOpen: boolean) => void;
};

export const KycModalBody = ({
  handleModalClose,
  onKycVerificationOpen,
}: KycModalBodyProps) => {
  const { activeStepIndex } = useOnboarding();
  const [isKycVerificationOpen, setIsKycVerificationOpen] = useState(false);
  const [verificationType, setVerificationType] = useState<TokenType>(
    TokenType.INDIVIDUAL
  );
  const [sumsubKey, setSumsubKey] = useState(Date.now());
  const lastTypeRef = useRef<TokenType>(TokenType.INDIVIDUAL);
  const statusKyc = useAppSelector((state) => state.user.userData.kyc?.status);
  const navigate = useNavigate();

  const redirectToWelcome = () => navigate("/kyc/welcome");

  const handleCloseModalAndRedirect = () => {
    onKycVerificationOpen?.(false);
    redirectToWelcome();
    handleModalClose();
  };

  const {
    accessToken,
    fetchAccessToken,
    config,
    options,
    accessTokenExpirationHandler,
    errorHandler,
    setAccessToken,
  } = useKYC({
    handleModalClose: handleCloseModalAndRedirect,
    initialVerificationType: verificationType,
    notMountListener: true,
  });

  const handleVerificationTypeChange = (type: TokenType) => {
    if (type !== verificationType) {
      setVerificationType(type);
      setAccessToken("");

      if (isKycVerificationOpen) {
        setSumsubKey(Date.now());
      }
    }
  };

  const handleOpenKycVerification = () => {
    // If the type has changed from the previous verification, ensure we have a clean state
    if (lastTypeRef.current !== verificationType) {
      setAccessToken("");
      setSumsubKey(Date.now());
      lastTypeRef.current = verificationType;
    }

    fetchAccessToken(verificationType);
    setIsKycVerificationOpen(true);
    onKycVerificationOpen?.(true);
  };

  const fadeInOutVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 10 },
  };

  useEffect(() => {
    if (statusKyc === "started") {
      handleCloseModalAndRedirect();
    }
  }, []);

  useEffect(
    () =>
      // Cleanup function to notify parent when component unmounts
      () => {
        onKycVerificationOpen?.(false);
      },
    [onKycVerificationOpen]
  );

  useEffect(() => {
    // Notify parent when isKycVerificationOpen changes
    if (!isKycVerificationOpen) {
      onKycVerificationOpen?.(false);
    }
  }, [isKycVerificationOpen, onKycVerificationOpen]);

  return !isKycVerificationOpen ? (
    <S.KycModalWrapper
      key={activeStepIndex}
      initial={activeStepIndex === 0 ? "visible" : "hidden"}
      animate="visible"
      exit={activeStepIndex === 0 ? "visible" : "exit"}
      variants={fadeInOutVariants}
      transition={{ duration: 0.3 }}
    >
      <S.KycModalContent>
        <S.SelectVerificationTypeHeader>
          Select Verification Type
        </S.SelectVerificationTypeHeader>
        <S.VerificationTypeContainer>
          <S.VerificationTypeOption
            selected={verificationType === TokenType.INDIVIDUAL}
            onClick={() => handleVerificationTypeChange(TokenType.INDIVIDUAL)}
          >
            <S.VerificationTypeTitle>
              Individual{" "}
              <S.VerificationTypeLabel>(KYC)</S.VerificationTypeLabel>
            </S.VerificationTypeTitle>
            <S.VerificationTypeDescription>
              For personal accounts
            </S.VerificationTypeDescription>
          </S.VerificationTypeOption>

          <S.VerificationTypeOption
            selected={verificationType === TokenType.COMPANY}
            onClick={() => handleVerificationTypeChange(TokenType.COMPANY)}
          >
            <S.VerificationTypeTitle>
              Business <S.VerificationTypeLabel>(KYB)</S.VerificationTypeLabel>
            </S.VerificationTypeTitle>
            <S.VerificationTypeDescription>
              For company or organization accounts
            </S.VerificationTypeDescription>
          </S.VerificationTypeOption>
        </S.VerificationTypeContainer>
      </S.KycModalContent>

      <S.InitKycButton onClick={handleOpenKycVerification}>
        Verify Identity
      </S.InitKycButton>
      <S.PoweredText>Powered by</S.PoweredText>
      <S.SumsubLogo src={sumsubLogo} alt="Sumsub logo" />
      <S.KycModalContent>
        We use Sumsub as a third-party identity verification services provider.
        Your personal data will be processed by Sumsub. We will only have access
        to the results of your identity verification. To learn more about
        processing of your personal data please consult our{" "}
        <span>Privacy Policy</span>, as well as the{" "}
        <span>Sumsub&apos;s Privacy Policy.</span>
      </S.KycModalContent>
    </S.KycModalWrapper>
  ) : (
    <S.KycModalWrapper
      isFullHeight
      key={isKycVerificationOpen ? 1 : 0}
      initial={activeStepIndex === 0 ? "visible" : "hidden"}
      animate="visible"
      exit={activeStepIndex === 0 ? "visible" : "exit"}
      variants={fadeInOutVariants}
      transition={{ duration: 0.3 }}
    >
      <S.Title>
        <S.TitleSpan>Identity </S.TitleSpan>
        verification
      </S.Title>
      {!accessToken ? (
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            minHeight: "80%",
            minWidth: "100vw",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "#FFF",
          }}
        >
          <div
            style={{
              zIndex: 1000,
              height: "100%",
              width: "100%",
              background: "#FFF",
            }}
          >
            <Spinner />
          </div>
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 60,
            height: "100%",
            width: "100%",
            overflowY: "auto",
            zIndex: 1001,
          }}
        >
          {accessToken && (
            <SumsubWebSdk
              key={sumsubKey}
              accessToken={accessToken}
              expirationHandler={accessTokenExpirationHandler}
              config={config}
              options={options}
              onError={errorHandler}
            />
          )}
        </div>
      )}
    </S.KycModalWrapper>
  );
};
